import { ReactNode } from 'react';
import { resolveServiceDisplayName } from '../constants/serviceHubs';
import { getNavSectionsForUserType, employeeNavSections, cityAdminNavSections } from './roleNavigation';

export const CORE_SERVICE_MODULES = ['transportation', 'porter', 'on_demand'] as const;
export const ADMIN_STAFF_TYPES = ['super_admin', 'client_admin', 'employee'] as const;

export interface AdminNavItem {
  label: string;
  path: string;
  icon: string;
  module?: string;
  modules?: string[];
  feature?: string;
  superAdminOnly?: boolean;
  userTypes?: string[];
  excludeUserTypes?: string[];
  requiredPermissions?: string[];
  /** Items sharing a group label render together under one collapsible submenu. */
  group?: string;
  groupIcon?: string;
}

export interface AdminNavSection {
  id: string;
  label: string;
  accent?: string;
  items: AdminNavItem[];
}

export const adminNavSections: AdminNavSection[] = [
  // ── Overview ──────────────────────────────────────────────────────────────
  {
    id: 'overview',
    label: 'Overview',
    items: [
      { label: 'Dashboard', path: '/dashboard', icon: 'Dashboard' },
      { label: 'Users & Admins', path: '/users', icon: 'People', requiredPermissions: ['settings.users.view'], excludeUserTypes: ['employee'] },
    ],
  },

  // ── Client Management ─────────────────────────────────────────────────────
  {
    id: 'tenant_master',
    label: 'Client Management',
    accent: '#6366f1',
    items: [
      { label: 'Tenants', path: '/tenants', icon: 'Business', superAdminOnly: true },
    ],
  },

  // ── Operations ────────────────────────────────────────────────────────────
  {
    id: 'operations',
    label: 'Operations',
    accent: '#0ea5e9',
    items: [
      { label: 'Dispatch Center', path: '/dispatcher', icon: 'LocalShipping', modules: ['transportation', 'porter'], requiredPermissions: ['dispatcher.dashboard.view', 'transportation.rides.view'] },
      { label: 'SOS Command', path: '/dispatcher/sos', icon: 'Warning', modules: ['transportation'], requiredPermissions: ['dispatcher.sos.view', 'transportation.rides.view'] },
      { label: 'Zones', path: '/zones', icon: 'Public', requiredPermissions: ['settings.masters.view'] },
      { label: 'Rental Management', path: '/transport/rental-management', icon: 'Schedule', modules: ['transportation'], requiredPermissions: ['transportation.rides.view'] },
      { label: 'Outstation Management', path: '/transport/outstation-management', icon: 'Route', modules: ['transportation'], requiredPermissions: ['transportation.rides.view'] },
    ],
  },

  // ── Partners ────────────────────────────────────────────────────────────
  {
    id: 'onboarding',
    label: 'Partners',
    accent: '#7c3aed',
    items: [
      { label: 'Partners', path: '/partners', icon: 'Handshake', requiredPermissions: ['partners.view'] },
      { label: 'Partner Subscriptions', path: '/partners/subscription-plans', icon: 'CardMembership', requiredPermissions: ['partners.view'] },
      { label: 'Documents', path: '/config/documents', icon: 'Description', requiredPermissions: ['documents.verify'] },
    ],
  },

  // ── Service Hubs ─────────────────────────────────────────────────
  {
    id: 'service_modules',
    label: 'Service Hubs',
    accent: '#0f766e',
    items: [
      { label: 'Transport', path: '/hubs/transport', icon: 'DirectionsCar', modules: ['transportation'], requiredPermissions: ['transportation.rides.view', 'transportation.dashboard.view'] },
      { label: 'Delivery', path: '/hubs/delivery', icon: 'DeliveryDining', modules: ['porter'], requiredPermissions: ['porter.orders.view', 'service_hub.bookings.view'] },
      { label: 'On Demand', path: '/hubs/on_demand', icon: 'Build', modules: ['on_demand'], requiredPermissions: ['service_hub.bookings.view'] },
    ],
  },

  // ── Customer App (mobile) ───────────────────────────────────────────────────
  {
    id: 'customer_app',
    label: 'Customer App',
    accent: '#e11d48',
    items: [
      { label: 'Home Banners', path: '/customer-app/home-banners', icon: 'Campaign', requiredPermissions: ['settings.masters.view'] },
      { label: 'Promos & Coupons', path: '/customer-app/promos-coupons', icon: 'LocalOffer', feature: 'promo', requiredPermissions: ['settings.masters.view'] },
    ],
  },

  // ── Referrals & Points ────────────────────────────────────────────────────
  {
    id: 'rewards',
    label: 'Referrals & Points',
    accent: '#16a34a',
    items: [
      { label: 'Referral Settings', path: '/rewards/referral-settings', icon: 'Share', userTypes: ['super_admin', 'client_admin'], requiredPermissions: ['marketing.rewards.view'] },
      { label: 'Referrals', path: '/rewards/referrals', icon: 'GroupAdd', userTypes: ['super_admin', 'client_admin'], requiredPermissions: ['marketing.rewards.view'] },
      { label: 'Points Settings', path: '/rewards/points-settings', icon: 'Stars', userTypes: ['super_admin', 'client_admin'], requiredPermissions: ['marketing.rewards.view'] },
      { label: 'Points Ledger', path: '/rewards/points', icon: 'ReceiptLong', userTypes: ['super_admin', 'client_admin'], requiredPermissions: ['marketing.rewards.view'] },    ],
  },

  // ── Finance & Reports ─────────────────────────────────────────────────────
  {
    id: 'finance',
    label: 'Finance & Reports',
    accent: '#f59e0b',
    items: [
      { label: 'Settlements', path: '/settlements', icon: 'Payments', requiredPermissions: ['analytics.reports.view'] },
      { label: 'Payment Transactions', path: '/payment-transactions', icon: 'CreditCard', requiredPermissions: ['analytics.reports.view'] },
      { label: 'Payout KYC', path: '/payouts/kyc', icon: 'VerifiedUser', group: 'Verification & Payouts', groupIcon: 'VerifiedUser', requiredPermissions: ['analytics.reports.view', 'partners.view'] },
      { label: 'Payout Bank Accounts', path: '/payouts/accounts', icon: 'AccountBalance', group: 'Verification & Payouts', requiredPermissions: ['analytics.reports.view', 'partners.view'] },
      { label: 'Partner Withdrawals', path: '/payouts/withdrawals', icon: 'AccountBalanceWallet', group: 'Verification & Payouts', requiredPermissions: ['analytics.reports.view', 'partners.view'] },
      { label: 'Payout Settings', path: '/payouts/settings', icon: 'Settings', group: 'Verification & Payouts', userTypes: ['super_admin', 'client_admin'], requiredPermissions: ['analytics.reports.view', 'partners.view'] },
      { label: 'Payout Reports', path: '/payouts/reports', icon: 'Assessment', requiredPermissions: ['analytics.reports.view', 'partners.view'] },
      {
        label: 'Transaction History',
        path: '/tenants/transactions',
        icon: 'ReceiptLong',
        userTypes: ['super_admin', 'client_admin'],
      },
      { label: 'Reports', path: '/reports', icon: 'Assessment', requiredPermissions: ['analytics.reports.view'] },
    ],
  },

  // ── Support ───────────────────────────────────────────────────────────────
  {
    id: 'support',
    label: 'Support',
    accent: '#8b5cf6',
    items: [
      { label: 'Helpdesk', path: '/helpdesk', icon: 'SupportAgent', requiredPermissions: ['helpdesk.tickets.view'] },
    ],
  },

  // ── Settings ──────────────────────────────────────────────────────────────
  {
    id: 'config',
    label: 'Settings',
    items: [
      { label: 'Master Data', path: '/config/masters', icon: 'Category', requiredPermissions: ['settings.masters.view'] },
      { label: 'Roles & Privileges', path: '/config/roles', icon: 'Security', requiredPermissions: ['settings.roles.view', 'settings.users.view'] },
      { label: 'Notifications', path: '/config/notifications', icon: 'Notifications', requiredPermissions: ['settings.users.view'] },
      { label: 'Payment Gateways', path: '/config/payments', icon: 'CreditCard', requiredPermissions: ['settings.settings.view'] },
      { label: 'SMS Gateways', path: '/config/sms', icon: 'Sms', requiredPermissions: ['settings.settings.view'] },
      { label: 'Translator Master', path: '/config/translations', icon: 'Translate', requiredPermissions: ['settings.masters.view'] },
      { label: 'App Versioning', path: '/config/app-versioning', icon: 'SystemUpdate', requiredPermissions: ['settings.settings.view'] },
      { label: 'Site Settings', path: '/config/settings', icon: 'Settings', requiredPermissions: ['settings.settings.view'] },
      { label: 'Audit Logs', path: '/audit-logs', icon: 'ManageSearch', requiredPermissions: ['settings.audit.view'] },
    ],
  },
];

function itemVisible(
  item: AdminNavItem,
  opts: {
    isSuperAdmin: boolean;
    userType?: string;
    enabledModules: string[];
    enabledFeatures: string[];
    hasAnyPermission: (permissions?: string[]) => boolean;
  },
): boolean {
  if (item.superAdminOnly && !opts.isSuperAdmin) return false;
  if (item.userTypes?.length && (!opts.userType || !item.userTypes.includes(opts.userType))) return false;
  if (item.excludeUserTypes?.length && opts.userType && item.excludeUserTypes.includes(opts.userType)) return false;
  if (item.modules?.length) {
    if (!opts.isSuperAdmin && !item.modules.some((m) => opts.enabledModules.includes(m))) return false;
  } else if (item.module && !opts.isSuperAdmin && !opts.enabledModules.includes(item.module)) {
    return false;
  }
  if (item.feature && !opts.isSuperAdmin && !opts.enabledFeatures.includes(item.feature)) return false;
  if (!opts.isSuperAdmin && item.requiredPermissions?.length && !opts.hasAnyPermission(item.requiredPermissions)) return false;
  return true;
}

export function resolveNavSections(userType?: string, roleSlugs?: string[]): AdminNavSection[] {
  if (userType === 'employee' && roleSlugs?.includes('city_admin')) {
    return cityAdminNavSections;
  }
  if (userType === 'super_admin' || userType === 'client_admin') return adminNavSections;
  if (userType === 'employee') return employeeNavSections;
  const roleSections = getNavSectionsForUserType(userType);
  if (roleSections.length) return roleSections;
  return [];
}

export function filterAdminNavSections(
  sections: AdminNavSection[],
  opts: {
    isSuperAdmin: boolean;
    userType?: string;
    enabledModules: string[];
    enabledFeatures: string[];
    hasAnyPermission: (permissions?: string[]) => boolean;
  },
): AdminNavSection[] {
  return sections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => itemVisible(item, opts)),
    }))
    .filter((section) => section.items.length > 0);
}

export function findNavLabel(pathname: string, sections: AdminNavSection[]): string | undefined {
  // Inside a service workspace, prefer the service name over the hub/module label
  // (e.g. /hubs/on_demand/home-services/... → "Services", not "On Demand").
  const hubServiceParts = pathname.split('/').filter(Boolean);
  if (hubServiceParts[0] === 'hubs' && hubServiceParts.length >= 3) {
    const serviceLabel = resolveServiceDisplayName(hubServiceParts[1], hubServiceParts[2]);
    if (serviceLabel) return serviceLabel;
  }

  const items = sections.flatMap((section) => section.items).sort((a, b) => {
    const aBase = a.path.split('?')[0];
    const bBase = b.path.split('?')[0];
    if (aBase === bBase) {
      // Prefer canonical routes (no query) when both match the same path
      return (a.path.includes('?') ? 1 : 0) - (b.path.includes('?') ? 1 : 0);
    }
    return bBase.length - aBase.length;
  });
  for (const item of items) {
    const basePath = item.path.split('?')[0];
    if (pathname === basePath || (basePath !== '/dashboard' && pathname.startsWith(`${basePath}/`))) {
      return item.label;
    }
  }
  if (pathname.startsWith('/transport/rental-management')) return 'Rental Management';
  if (pathname.startsWith('/transport/outstation-management')) return 'Outstation Management';
  if (pathname.startsWith('/hubs/transport')) return 'Transport';
  if (pathname.startsWith('/hubs/delivery')) return 'Delivery';
  if (pathname.startsWith('/hubs/on_demand')) return 'On Demand';
  if (pathname.startsWith('/hubs/')) return 'Service Hub';
  if (pathname.startsWith('/my-profile')) return 'My Profile';
  if (pathname.startsWith('/account')) return 'My Account';
  if (pathname.startsWith('/onboarding')) return 'Onboarding';
  if (pathname.startsWith('/partners/subscription-plans')) return 'Partner Subscriptions';
  if (pathname.startsWith('/partners')) return 'Partners';
  if (pathname.startsWith('/config/documents')) return 'Documents';
  if (pathname.startsWith('/zones')) return 'Zones';
  if (pathname.startsWith('/customer-app/promos-coupons')) return 'Promos & Coupons';
  if (pathname.startsWith('/customer-app/home-banners')) return 'Home Banners';
  if (pathname.startsWith('/config/app-versioning')) return 'App Versioning';
  if (pathname.startsWith('/config/app-promos')) return 'Promos & Coupons';
  if (pathname.startsWith('/config/app-masters')) return 'Home Banners';
  if (pathname.startsWith('/reports/')) return 'Report';
  if (pathname.startsWith('/reports')) return 'Reports';
  if (pathname.startsWith('/config')) return 'Settings';
  if (pathname.startsWith('/vendor')) return 'Vendor';
  return undefined;
}

export type NavIconResolver = (name: string) => ReactNode;
